import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
// @mui
import List from '@mui/material/List';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { Stack, Typography, Button } from '@mui/material';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/auth/hooks';
import useBrandName from 'src/hooks/use-brand-name';
import { AGENT } from 'src/_config/brandConfig';
// routes
import { usePathname } from 'src/routes/hooks';
// components
import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import Scrollbar from 'src/components/scrollbar';
import OnTimeGuaranteeModal from 'src/components/custom-dialog/on-time-guarantee-modal';
//
import NavList from './nav-list';

// ----------------------------------------------------------------------

export default function NavMobile({ offsetTop, data }) {
  const pathname = usePathname();
  const theme = useTheme();
  const nav = useBoolean();
  const { authenticated } = useAuthContext();
  const { isTryVisa } = useBrandName();
  const [openGuaranteeModal, setOpenGuaranteeModal] = React.useState(false);

  useEffect(() => {
    if (nav.value) {
      nav.onFalse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      <IconButton
        onClick={nav.onTrue}
        sx={{
          color: 'common.white',
          ...(offsetTop && {
            color: 'text.primary',
          }),
          padding: 0,
        }}
      >
        <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
      </IconButton>

      <Drawer
        open={nav.value}
        onClose={nav.onFalse}
        PaperProps={{
          style: {
            backgroundColor: theme.palette.secondary.main,
            width: 260,
            boxShadow: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 3,
            }}
          >
            <Logo />
            <IconButton onClick={nav.onFalse} sx={{ color: 'common.white', width: 28, height: 28 }}>
              <SvgColor src="/assets/icons/navbar/ic_close.svg" />
            </IconButton>
          </Box>

          <Scrollbar sx={{ flexGrow: 1 }}>
            <List component="nav" disablePadding>
              {data.map((link) => (
                <NavList key={link.title} item={link} />
              ))}
            </List>
          </Scrollbar>

          <Stack
            direction="column"
            spacing={2}
            sx={{
              px: 2,
              py: 3,
              mt: 'auto', // This pushes the stack to the bottom
            }}
          >
            {isTryVisa && !authenticated && (
              <Button
                variant="text"
                color="inherit"
                size="medium"
                fullWidth
                sx={{
                  height: 40,
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
                onClick={() => {
                  window.location.href = AGENT.dashboardUrl;
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box component="img" src="/assets/icons/navbar/agent.svg" />
                  <Typography
                    sx={{ color: '#FFFFFF', fontSize: 12, fontWeight: 600 }}
                    typography="body1"
                    color="inherit"
                  >
                    For Agents
                  </Typography>
                </Stack>
              </Button>
            )}

            <Button
              variant="text"
              color="inherit"
              size="medium"
              fullWidth
              sx={{
                height: 40,
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
              onClick={() => setOpenGuaranteeModal(true)}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box component="img" src="/assets/icons/navbar/guarantee.svg" />
                <Typography
                  sx={{ color: '#FFFFFF', fontSize: 12, fontWeight: 600 }}
                  typography="body1"
                  color="inherit"
                >
                  On Time Guaranteed
                </Typography>
              </Stack>
            </Button>
          </Stack>
        </Box>
      </Drawer>
      <OnTimeGuaranteeModal
        open={openGuaranteeModal}
        onClose={() => setOpenGuaranteeModal(false)}
      />
    </>
  );
}

NavMobile.propTypes = {
  data: PropTypes.array,
  offsetTop: PropTypes.bool,
};
